import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { QUIZ_QUESTIONS } from '../../utils/valuationConstants';

/**
 * ValuationQuizModal - 10-question quiz to recommend best valuation method
 * Collects user answers and triggers calculation
 */
const ValuationQuizModal = ({ isOpen, onClose, quizAnswers, onAnswerChange, onCalculateResult }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Find Your Best Valuation Method</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <X size={24} />
            </button>
          </div>

          <p className="text-gray-600 mb-6">
            Answer these 10 questions and we'll recommend the best valuation method for your business.
          </p>

          <div className="space-y-6">
            {QUIZ_QUESTIONS.map((question) => (
              <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">
                  {question.question}
                </h3>
                <div className="space-y-2">
                  {question.options.map(option => (
                    <label
                      key={option.value}
                      className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer transition-colors"
                    >
                      <input
                        type="radio"
                        name={question.id}
                        value={option.value}
                        checked={quizAnswers[question.id] === option.value}
                        onChange={() => onAnswerChange(question.id, option.value, option.method)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={onCalculateResult}
            disabled={Object.keys(quizAnswers).length < QUIZ_QUESTIONS.length}
            className="mt-6 w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold min-h-[44px]"
          >
            Get My Recommendation
          </button>
        </div>
      </div>
    </div>
  );
};

ValuationQuizModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  quizAnswers: PropTypes.object.isRequired,
  onAnswerChange: PropTypes.func.isRequired,
  onCalculateResult: PropTypes.func.isRequired
};

export default ValuationQuizModal;
